import styled from 'styled-components'
import { CREATE_PATH, LOCK_HOME, lockPath } from 'views/LockTools/config'
import type { LockPhase, LockSummary, Totals } from 'views/LockTools/types'
import TokenLogo from 'views/TokenPages/components/TokenLogo'
import { ListTick } from 'views/TokenPages/components/VerifyTicks'
import { tokenHref } from 'views/TokenPages/config'
import { fmtUsd, priceParts } from 'views/TokenPages/format'
import type { ListItem } from 'views/TokenPages/types'
import { lockTotalsLine, lockWhen } from './logic'
import { Chip, DISPLAY, Ellipsis, Fluent, HUES, HeadLink, Hue, MONO, MUTED, Panel, PanelHead, RowLink, SNOW, SNOW_2, Skel } from './ui'

/*
 * The proof the place is alive, below the tools: the tokens trading on CADINUdex (/api/tokens/list, the /tokens page's own list) and the
 * newest locks (/api/locks, the lock explorer's own index). Both lists have a fixed number of row slots of a fixed height, so a list that
 * arrives late, short or not at all never moves what is under it; a missing row becomes the row that tells a project how to get there.
 */
const HOT_SLOTS = 5
const HOT_H = 56
const LOCK_SLOTS = 4
const LOCK_H = 70 // three short lines; side by side the grid stretches the twin panels to one height

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 16px;
  ${({ theme }) => theme.mediaQueries.lg} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px;
  }
`
/** a fill row takes every slot that is left, so a short list ends in one call to action instead of an empty gap */
const GAP = 6
const span = (k: number, h: number) => ({ height: k * h + (k - 1) * GAP, style: { gridRow: `span ${k}` } })
const List = styled.div<{ $n: number; $h: number }>`
  display: grid;
  grid-template-rows: repeat(${({ $n }) => $n}, ${({ $h }) => $h}px);
  gap: ${GAP}px;
`
const SkelRow = styled.div<{ $h: number }>`
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) 88px;
  align-items: center;
  column-gap: 12px;
  height: ${({ $h }) => $h}px;
  padding: 0 10px;
`
const skeletons = (n: number, h: number) =>
  Array.from({ length: n }, (_, i) => (
    <SkelRow key={i} $h={h} aria-hidden>
      <Skel $w={36} $h={36} $r={999} />
      <span style={{ display: 'grid', gap: 6 }}>
        <Skel $w="55%" $h={14} />
        <Skel $w="35%" $h={10} />
      </span>
      <Skel $w={80} $h={14} />
    </SkelRow>
  ))

const TokenRow = styled(RowLink)`
  grid-template-columns: 20px 36px minmax(0, 1fr) auto;
  .rank {
    font-family: ${MONO};
    font-size: 12px;
    color: ${MUTED};
    text-align: center;
  }
  .sym {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    font-family: ${DISPLAY};
    font-weight: 800;
    font-size: 16px;
    line-height: 20px;
  }
  .name {
    font-size: 12px;
    line-height: 16px;
    color: ${MUTED};
  }
  .right {
    text-align: right;
  }
  .price {
    display: block;
    font-family: ${MONO};
    font-weight: 700;
    font-size: 13px;
    line-height: 18px;
    color: ${SNOW};
    white-space: nowrap;
  }
  .price sub {
    font-size: 0.75em;
    vertical-align: -0.25em;
  }
  .vol {
    display: block;
    font-size: 12px;
    line-height: 16px;
    color: ${SNOW_2};
    white-space: nowrap;
  }
`
const Price = ({ n }: { n: number | null }) => {
  const p = priceParts(n)
  if (p.kind === 'none') return <>—</>
  if (p.kind === 'plain') return <>${p.text}</>
  return (
    <>
      ${p.lead}
      <sub>{p.zeros}</sub>
      {p.digits}
    </>
  )
}

const FillRow = styled(RowLink)<{ $hue: Hue }>`
  grid-template-columns: 36px minmax(0, 1fr) auto;
  border: 1px dashed rgba(${({ $hue }) => HUES[$hue].rgb}, 0.45);
  background: rgba(${({ $hue }) => HUES[$hue].rgb}, 0.05);
  .t {
    font-weight: 700;
    font-size: 14px;
    line-height: 18px;
  }
  .s {
    font-size: 12px;
    line-height: 16px;
    color: ${MUTED};
    /* a fill row that takes two slots or more has room for the whole line */
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
  .go {
    font-weight: 800;
    font-size: 13px;
    color: ${({ $hue }) => HUES[$hue].text};
    white-space: nowrap;
  }
`
const Note = styled.p`
  margin: 0;
  display: flex;
  align-items: center;
  padding: 0 10px;
  font-size: 14px;
  line-height: 20px;
  color: ${SNOW_2};
`

export function HotTokens({ rows, loading, failed }: { rows: ListItem[]; loading: boolean; failed: boolean }) {
  const slots: JSX.Element[] = loading
    ? skeletons(HOT_SLOTS, HOT_H)
    : rows.slice(0, HOT_SLOTS).map((t, i) => (
        <TokenRow key={t.address} $h={HOT_H} href={tokenHref(t.address)} data-testid="home-hot-row">
          <span className="rank">{i + 1}</span>
          <TokenLogo srcs={[t.logoUrl]} address={t.address} symbol={t.symbol} size={36} />
          <span style={{ minWidth: 0 }}>
            <span className="sym">
              <Ellipsis>{t.symbol}</Ellipsis>
              <ListTick address={t.address} verified={t.verified} teamVerified={t.teamVerified} testId="home-hot-tick" />
            </span>
            <Ellipsis className="name">{t.name}</Ellipsis>
          </span>
          <span className="right">
            <span className="price">
              <Price n={t.priceUsd} />
            </span>
            <span className="vol">{t.volume24hUsd !== null ? `Vol 24h ${fmtUsd(t.volume24hUsd)}` : 'No volume data'}</span>
          </span>
        </TokenRow>
      ))
  if (!loading && failed) slots.push(<Note key="failed">Token data did not load. The tokens page has the full list.</Note>)
  if (!loading && slots.length < HOT_SLOTS) {
    const f = span(HOT_SLOTS - slots.length, HOT_H)
    slots.push(
      <FillRow key="fill" $h={f.height} style={f.style} $hue="blue" href="/liquidity" data-testid="home-hot-fill">
        <Fluent name="sparkles" size={32} />
        <span style={{ minWidth: 0 }}>
          <Ellipsis className="t">Your token here</Ellipsis>
          <Ellipsis className="s">Add a CADINUdex pool to be listed</Ellipsis>
        </span>
        <span className="go">Add liquidity →</span>
      </FillRow>,
    )
  }
  return (
    <Panel $glow="amber" aria-labelledby="home-hot-title" data-testid="home-hot" data-state={loading ? 'loading' : failed ? 'error' : 'ready'}>
      <PanelHead>
        <Fluent name="fire" size={32} />
        <span className="grow">
          <h2 id="home-hot-title">Hot on CADINUdex</h2>
          <span className="facts">By 24 h volume, trades and liquidity</span>
        </span>
        <HeadLink href="/tokens">All tokens</HeadLink>
      </PanelHead>
      <List $n={HOT_SLOTS} $h={HOT_H}>
        {slots}
      </List>
    </Panel>
  )
}

const PHASE: Record<LockPhase, { label: string; hue: Hue }> = {
  locked: { label: 'Locked', hue: 'ice' },
  vesting: { label: 'Vesting', hue: 'violet' },
  due: { label: 'Unlock due', hue: 'amber' },
  released: { label: 'Released', hue: 'blue' },
}
const LockRow = styled(RowLink)`
  grid-template-columns: 44px minmax(0, 1fr) auto;
  .logos {
    position: relative;
    width: 44px;
    height: 36px;
  }
  .logos > :nth-child(2) {
    position: absolute;
    left: 16px;
    top: 8px;
  }
  .title {
    font-family: ${DISPLAY};
    font-weight: 800;
    font-size: 16px;
    line-height: 20px;
  }
  .sub,
  .when {
    font-size: 12px;
    line-height: 16px;
    color: ${MUTED};
  }
  .when {
    color: ${SNOW_2};
  }
  .right {
    display: grid;
    justify-items: end;
    gap: 6px;
  }
  .usd {
    font-family: ${MONO};
    font-weight: 700;
    font-size: 13px;
    line-height: 18px;
    white-space: nowrap;
  }
`

export function RecentLocks({ rows, totals, loading, failed, now }: { rows: LockSummary[]; totals: Totals | null; loading: boolean; failed: boolean; now: number | null }) {
  const slots: JSX.Element[] = loading
    ? skeletons(LOCK_SLOTS, LOCK_H)
    : rows.slice(0, LOCK_SLOTS).map((l) => {
        const ph = PHASE[l.phase]
        return (
          <LockRow key={`${l.type}-${l.id}`} $h={LOCK_H} href={lockPath(l.type, l.id)} data-testid="home-lock-row">
            <span className="logos">
              {l.tokens.slice(0, 2).map((t) => (
                <TokenLogo key={t.address} address={t.address} symbol={t.symbol} size={l.tokens.length > 1 ? 28 : 36} />
              ))}
            </span>
            <span style={{ minWidth: 0 }}>
              <Ellipsis className="title">{l.title}</Ellipsis>
              <Ellipsis className="sub">{l.subtitle}</Ellipsis>
              <Ellipsis className="when">{now === null ? ' ' : lockWhen(l, now)}</Ellipsis>
            </span>
            <span className="right">
              <Chip $hue={ph.hue}>{ph.label}</Chip>
              <span className="usd">{l.valueUsd !== null ? fmtUsd(l.valueUsd) : '—'}</span>
            </span>
          </LockRow>
        )
      })
  if (!loading && failed) slots.push(<Note key="failed">The lock list did not load. The lock explorer has every lock.</Note>)
  if (!loading && slots.length < LOCK_SLOTS) {
    const f = span(LOCK_SLOTS - slots.length, LOCK_H)
    slots.push(
      <FillRow key="fill" $h={f.height} style={f.style} $hue="ice" href={CREATE_PATH} data-testid="home-lock-fill">
        <Fluent name="lock" size={32} />
        <span style={{ minWidth: 0 }}>
          <Ellipsis className="t">Lock your tokens or LP</Ellipsis>
          <Ellipsis className="s">Each lock gets a public page</Ellipsis>
        </span>
        <span className="go">Lock →</span>
      </FillRow>,
    )
  }
  const facts = lockTotalsLine(totals) ?? 'Newest first, read from the lockers'
  return (
    <Panel $glow="ice" aria-labelledby="home-locks-title" data-testid="home-locks" data-state={loading ? 'loading' : failed ? 'error' : 'ready'}>
      <PanelHead>
        <Fluent name="lock" size={32} />
        <span className="grow">
          <h2 id="home-locks-title">Recent locks</h2>
          <span className="facts">{facts}</span>
        </span>
        <HeadLink href={LOCK_HOME}>Lock explorer</HeadLink>
      </PanelHead>
      <List $n={LOCK_SLOTS} $h={LOCK_H}>
        {slots}
      </List>
    </Panel>
  )
}

export const BoardGrid = Grid
